import {
    ApiCrowLink,
    ApiExperiment,
    ApiShopperLandingPage,
    GetCrowLinkResponse,
} from '@storefronts/common-types'
import type {
    GetStaticPaths,
    GetStaticProps,
    GetStaticPropsContext,
    GetStaticPropsResult,
    NextPage,
} from 'next'
import Head from 'next/head'
import Script from 'next/script'
import { useMemo } from 'react'
import { validate as isValidUUID } from 'uuid'
import { useAnalytics } from '../../storefront/analytics/analytics'
import { LandingPage } from '../../storefront/components/landing-page/LandingPage'
import { PixelLoader } from '../../storefront/components/pixels/PixelLoader'
import { DesktopRedirectScript } from '../../storefront/components/shared/DesktopRedirectScript'
import {
    isNotFoundApiResponse,
    shopperInitialLandingPageUrl,
} from '../../storefront/utils/serverSideHelpers'
import * as fs from 'fs'
import path from 'path'
import * as terser from 'terser'

const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASEURL

export const getStaticPaths: GetStaticPaths = async () => {
    return {
        paths: [],
        fallback: 'blocking',
    }
}

const getLandingPage = async (
    linkHash: string,
): Promise<ApiShopperLandingPage | null> => {
    const res = await fetch(shopperInitialLandingPageUrl(apiBaseUrl, linkHash))
    const resp = await res.json()
    if (resp.isError) {
        return null
    }
    return resp.result.landingPage as ApiShopperLandingPage
}

const getMinifiedJS = async (filePath: string): Promise<string> => {
    try {
        // Read the file content into a string
        const code = await fs.promises.readFile(filePath, 'utf8')

        // Minify the code using Terser API
        const result = await terser.minify(code)

        // Return the minified code
        return result.code || ''
    } catch (err) {
        console.error('Error processing file:', err)
        throw err
    }
}

export const getStaticProps: GetStaticProps = async (
    context: GetStaticPropsContext,
): Promise<GetStaticPropsResult<CrowLinkPageProps>> => {
    const linkHash = context.params?.linkHash as string

    // A malformed hash (e.g. a relative URL like `x` from a broken
    // `<img src="x">` in custom HTML) can never match a real crow link.
    // Return a 404 instead of throwing, which would surface as a noisy 500.
    if (!isValidUUID(linkHash)) {
        return { notFound: true, revalidate: 20 }
    }

    try {
        const res = await fetch(`${apiBaseUrl}/external/crowlink/${linkHash}`)
        const resp = await res.json()

        if (resp.isError) {
            // A non-existent crow link is a 404, not a server error. Only
            // genuine backend failures fall through to the catch below.
            if (isNotFoundApiResponse(resp)) {
                return { notFound: true, revalidate: 20 }
            }
            console.error(resp.message)
            throw new Error(resp.message)
        }

        const getCrowLinkResponse: GetCrowLinkResponse = resp.result
        const crowLink: ApiCrowLink = getCrowLinkResponse.crowLink

        const minifiedJs = await getMinifiedJS(
            path.join(process.cwd(), 'src/modules/redirectScript.js'),
        )

        if (crowLink.destinationType === 'EXPERIMENT') {
            return {
                props: {
                    experiment: crowLink.experiment,
                    crowLinkHash: linkHash,
                    siteName: crowLink.client.siteName,
                    minifiedJs,
                    icon: null,
                },
                revalidate: 20,
            }
        }

        if (
            crowLink.destinationType === 'LANDINGPAGE' &&
            crowLink.landingPageHash
        ) {
            const landingPage = await getLandingPage(crowLink.landingPageHash)

            if (!landingPage) {
                return { props: {}, revalidate: 20 }
            }

            return {
                props: {
                    landingPage,
                    icon: landingPage.client.favicon ?? null,
                    crowLinkHash: linkHash,
                    siteName: crowLink.client.siteName,
                },
                revalidate: 20,
            }
        }

        if (crowLink.destinationType === 'URL' && crowLink.destinationUrl) {
            return {
                props: {
                    destinationUrl: crowLink.destinationUrl,
                    crowLinkHash: linkHash,
                    siteName: crowLink.client.siteName,
                    icon: null,
                },
                revalidate: 20,
            }
        }

        return { props: {}, revalidate: 20 }
    } catch (error) {
        // A genuine server failure shouldn't surface as a noisy 500. Send the
        // shopper to the home page instead — but client-side, not via a
        // getStaticProps `redirect` result. Next.js does NOT ISR-revalidate
        // cached redirects (vercel/next.js#39704), so a transient backend blip
        // would pin a valid crow link to the home redirect until the next
        // deploy. A `props` result does revalidate, so `revalidate: 20` lets
        // this self-heal once the backend recovers.
        console.error(error)
        return { props: { redirectToHome: true }, revalidate: 20 }
    }
}

type CrowLinkPageProps = {
    destinationUrl?: string | null
    landingPage?: ApiShopperLandingPage | null
    icon?: string | null
    experiment?: ApiExperiment | null
    crowLinkHash?: string
    siteName?: string
    minifiedJs?: string
    redirectToHome?: boolean
}

const CrowLinkPage: NextPage<CrowLinkPageProps> = ({
    destinationUrl,
    landingPage,
    icon,
    experiment,
    crowLinkHash,
    siteName,
    minifiedJs,
    redirectToHome,
}) => {
    useAnalytics(landingPage)

    // Get video poster image to preload for LCP optimization
    const videoPosterImage = useMemo(() => {
        if (!landingPage) return null

        // Only preload poster if video is the hero content (JUST_CONTENT_NO_OVERLAY)
        const isVideoHero = landingPage.ux === 'JUST_CONTENT_NO_OVERLAY'
        if (!isVideoHero) return null

        const firstVideo = landingPage.defaultPlaylist?.videos?.[0]
        if (!firstVideo?.imageUrl) return null
        return firstVideo.imageUrl
    }, [landingPage])

    // Enhanced utility function to append multiple tracking parameters
    const appendTrackingParamsFunction = `
        function appendTrackingParams(url, params) {
            if (!url) return url;
            
            // Parse the URL
            var urlObj = new URL(url.startsWith('http') ? url : window.location.protocol + '//' + window.location.host + url);
            
            // Get current URL search params
            var currentParams = new URLSearchParams(window.location.search);
            
            // Merge params: first add current URL params, then override with tracking params
            for (const [key, value] of currentParams) {
                urlObj.searchParams.set(key, value);
            }
            
            // Add tracking params (these take precedence)
            for (const key in params) {
                if (params[key]) urlObj.searchParams.set(key, params[key]);
            }
            
            return urlObj.toString();
        }
    `

    if (redirectToHome) {
        return (
            <Script id={'redirect'} strategy={'beforeInteractive'}>
                {`window.location.href = '/';`}
            </Script>
        )
    }

    if (experiment) {
        return (
            <Script id={'redirect'} strategy={'beforeInteractive'}>
                {`
                    const variants = ${JSON.stringify(experiment.variants)};
                    const crowLinkHash = ${JSON.stringify(crowLinkHash)};
                    const experimentId = ${JSON.stringify(experiment.id)};
                    ${minifiedJs}
                    performRedirect(variants, crowLinkHash, experimentId, '${siteName}');
                `}
            </Script>
        )
    }

    if (destinationUrl) {
        return (
            <Script id={'redirect'} strategy={'beforeInteractive'}>
                {`
                    ${appendTrackingParamsFunction}
                    
                    // Prepare tracking parameters with just the crowlink hash
                    var trackingParams = { 'crow_link_hash': ${JSON.stringify(crowLinkHash)} };
                    
                    // Redirect with tracking params and preserve existing query params
                    window.location.href = appendTrackingParams('${destinationUrl}', trackingParams);
                `}
            </Script>
        )
    }

    if (landingPage)
        return (
            <>
                <Head>
                    <title>
                        {landingPage.title || landingPage.client.name}
                    </title>
                    {icon && <link rel="icon" href={icon} />}
                    {/* Preconnect to Cloudinary for faster image loading */}
                    <link rel="preconnect" href="https://res.cloudinary.com" />
                    <link
                        rel="dns-prefetch"
                        href="https://res.cloudinary.com"
                    />
                    {/* Preload video poster image for LCP optimization (when video is hero) */}
                    {videoPosterImage && (
                        <link
                            rel="preload"
                            as="image"
                            href={videoPosterImage}
                            fetchPriority="high"
                        />
                    )}
                </Head>
                <DesktopRedirectScript landingPage={landingPage} />
                <LandingPage landingPage={landingPage} />
                <PixelLoader landingPage={landingPage} />
            </>
        )

    return (
        <div
            style={{
                width: '100vw',
                height: '100vh',
                backgroundColor: 'black',
            }}
        ></div>
    )
}

export default CrowLinkPage
